import { CaretUpDownIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import { GenericTable } from '@/modules/common';

import { EventProduct } from '../types';
import { InventoryAmountInput } from './inventory-amount-input';
import { InventoryRemoveButton } from './inventory-remove-button';

interface InventoryTableProps {
  eventId: string;
  stationId: string;
  products: EventProduct[];
}

export function InventoryTable({
  eventId,
  products,
  stationId,
}: InventoryTableProps) {
  const columns: ColumnDef<EventProduct>[] = [
    {
      size: 200,
      accessorKey: 'category',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Category
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.category}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-2">{row.original.name}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'nickname',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Nickname
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.nickname}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'styleNumber',
      header: 'Style Number',
    },
    {
      size: 200,
      accessorKey: 'size',
      header: 'Size',

      cell: ({ row }) => {
        return (
          <span className="line-clamp-1">
            {row.original?.size?.size || 'Unknown'}
          </span>
        );
      },
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      cell: ({ row }) => (
        <InventoryAmountInput
          eventId={eventId}
          stationId={stationId}
          productId={row.original.id}
          sizeId={row.original?.size?.id}
          defaultValue={row.original.totalAmount.toString()}
        />
      ),
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => (
        <InventoryRemoveButton
          eventId={eventId}
          stationId={stationId}
          productId={row.original.id}
          sizeId={row.original?.size?.id}
        />
      ),
    },
  ];

  return (
    <GenericTable
      columns={columns}
      name="inventory"
      data={products}
      searchable
      searchPlaceholder="Search by category, name, or nickname"
      searchColumns={['category', 'name', 'nickname']}
      emptyCellClassName="h-20"
      emptyDescription="No inventory added. Please add new one."
      tableRowClassName="hover:bg-transparent"
    />
  );
}
